import React, { useState, FormEvent } from "react";
import { Modal } from "../ui/modal";
import { toast } from "react-hot-toast";
import { NoShow } from "../../hooks/useApi";
import { UserX, Users, User, MessageSquare, AlertCircle } from "lucide-react";

interface NoShowModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservationId: number;
  maxAdults: number;
  maxChildren: number;
  onSubmit: (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }) => Promise<void>;
}

const NoShowModal: React.FC<NoShowModalProps> = ({
  isOpen,
  onClose,
  reservationId,
  maxAdults,
  maxChildren,
  onSubmit
}) => {
  const [missingAdults, setMissingAdults] = useState<number>(0);
  const [missingChildren, setMissingChildren] = useState<number>(0);
  const [justificacion, setJustificacion] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    missingAdults?: string;
    missingChildren?: string;
    justificacion?: string;
    general?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: {
      missingAdults?: string;
      missingChildren?: string;
      justificacion?: string;
      general?: string;
    } = {};
    
    // Validate adults
    if (missingAdults < 0) {
      newErrors.missingAdults = "El número de adultos no puede ser negativo";
    } else if (missingAdults > maxAdults) {
      newErrors.missingAdults = `No puede exceder el máximo de ${maxAdults} adultos`;
    }
    
    // Validate children
    if (missingChildren < 0) {
      newErrors.missingChildren = "El número de niños no puede ser negativo";
    } else if (missingChildren > maxChildren) {
      newErrors.missingChildren = `No puede exceder el máximo de ${maxChildren} niños`;
    }
    
    // Validate at least one person is missing
    if (missingAdults === 0 && missingChildren === 0) {
      newErrors.general = "Debe indicar al menos una persona ausente";
    }
    
    // Validate justification is provided if needed
    if ((missingAdults > 0 || missingChildren > 0) && !justificacion.trim()) {
      newErrors.justificacion = "Debe proporcionar una justificación";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSubmit({
        reservationId,
        missingAdults,
        missingChildren,
        justificacion: justificacion.trim() ? justificacion : undefined
      });
      
      // Reset form
      setMissingAdults(0);
      setMissingChildren(0);
      setJustificacion("");
      onClose();
    } catch (error) {
      console.error("Error registering no show:", error);
      
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Error al registrar el no show");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar No Show"
      size="md"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <div className="space-y-5">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-500 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-300 mb-1">Información</h3>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Use este formulario para registrar pasajeros que no se presentaron a la actividad.
                Si todos los pasajeros están ausentes, la reserva se marcará automáticamente como "No Show".
              </p>
            </div>
          </div>
          
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Users className="w-4 h-4 mr-1.5 text-amber-500 dark:text-amber-400" />
              Adultos Ausentes
            </label>
            <div className="flex flex-col">
              <div className="relative rounded-md shadow-sm">
                <input
                  type="number"
                  min="0"
                  max={maxAdults}
                  value={missingAdults}
                  onChange={(e) => setMissingAdults(parseInt(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-amber-500 focus:ring-amber-500 dark:focus:border-amber-500 dark:focus:ring-amber-400"
                />
              </div>
              {errors.missingAdults && (
                <p className="mt-1.5 flex items-center text-xs text-red-600 dark:text-red-400">
                  <AlertCircle className="w-3 h-3 mr-1" /> 
                  {errors.missingAdults}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Máximo: {maxAdults} adultos</p>
            </div>
          </div>
          
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <User className="w-4 h-4 mr-1.5 text-amber-500 dark:text-amber-400" />
              Niños Ausentes
            </label>
            <div className="flex flex-col">
              <input
                type="number"
                min="0"
                max={maxChildren}
                value={missingChildren}
                onChange={(e) => setMissingChildren(parseInt(e.target.value) || 0)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-amber-500 focus:ring-amber-500 dark:focus:border-amber-500 dark:focus:ring-amber-400"
              />
              {errors.missingChildren && (
                <p className="mt-1.5 flex items-center text-xs text-red-600 dark:text-red-400">
                  <AlertCircle className="w-3 h-3 mr-1" /> 
                  {errors.missingChildren}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Máximo: {maxChildren} niños</p>
            </div>
          </div>
          
          {errors.general && (
            <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg flex items-start">
              <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{errors.general}</p>
            </div>
          )}
          
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <MessageSquare className="w-4 h-4 mr-1.5 text-amber-500 dark:text-amber-400" />
              Justificación <span className="text-red-500">*</span>
            </label>
            <textarea
              value={justificacion}
              onChange={(e) => setJustificacion(e.target.value)}
              rows={3}
              className="block w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-amber-500 focus:ring-amber-500 dark:focus:border-amber-500 dark:focus:ring-amber-400"
              placeholder="Explique por qué los pasajeros no se presentaron..."
            />
            {errors.justificacion && (
              <p className="mt-1.5 flex items-center text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="w-3 h-3 mr-1" /> 
                {errors.justificacion}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex justify-end pt-4 gap-3 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 dark:focus:ring-offset-gray-900"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 dark:focus:ring-offset-gray-900 disabled:opacity-50 dark:bg-amber-600 dark:hover:bg-amber-700"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <UserX className="w-4 h-4 mr-1.5" />
                <span>Registrar No Show</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default NoShowModal; 